
import { useState, useCallback, useEffect } from "react";
import { DistanceState, Position } from "@/types/locationTypes";
import { calculateDistance } from "@/utils/golfUtils";
import { useGeolocationPermission } from "./useGeolocationPermission";
import { usePositionTracking } from "./usePositionTracking";
import { useLocationNotifications } from "./useLocationNotifications";

/**
 * Hook for handling location tracking and distance calculation
 */
export const useLocationTracking = () => {
  const { isGeolocationAvailable, permissionGranted } = useGeolocationPermission();
  const { getCurrentPosition, watchPosition } = usePositionTracking();
  const { 
    notifyTrackingStarted, 
    notifyDistanceCalculated, 
    notifyReset, 
    notifyError 
  } = useLocationNotifications();
  
  const [state, setState] = useState<DistanceState>({
    startPosition: null,
    endPosition: null,
    distance: null,
    isTracking: false,
    error: null,
    progress: null,
  });

  // Handle position tracking during distance measurement
  const startPositionTracking = useCallback(() => {
    if (!isGeolocationAvailable || !state.isTracking || !state.startPosition) return;
    
    // Success function for each position update
    const success = (currentPosition: Position) => {
      // Calculate current distance from start position
      const currentDistance = calculateDistance(state.startPosition!, currentPosition);
      
      // Update state with new position and distance
      setState(prev => ({
        ...prev,
        progress: currentPosition,
        distance: currentDistance
      }));
    };
    
    // Error function
    const error = (err: GeolocationPositionError) => {
      console.error("Position tracking error:", err);
    };
    
    // Start tracking and get cleanup function
    return watchPosition(success, error);
  }, [isGeolocationAvailable, state.isTracking, state.startPosition, watchPosition]);
  
  // Activate position tracking when tracking starts
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    
    if (state.isTracking && state.startPosition) {
      cleanup = startPositionTracking();
    }
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [state.isTracking, state.startPosition, startPositionTracking]);

  // Start tracking (start position)
  const startTracking = useCallback(async () => {
    try {
      const position = await getCurrentPosition();
      setState((prev) => ({
        ...prev,
        startPosition: position,
        endPosition: null,
        progress: position, // Initialize progress with the start position
        distance: 0, // Start with 0 distance
        isTracking: true,
        error: null,
      }));
      
      notifyTrackingStarted();
    } catch (error: any) {
      setState((prev) => ({ ...prev, error: error.message, isTracking: false }));
      notifyError(error.message);
    }
  }, [getCurrentPosition, notifyTrackingStarted, notifyError]);

  // End tracking (end position) and calculate distance
  const stopTracking = useCallback(async () => {
    if (!state.startPosition) {
      setState((prev) => ({
        ...prev,
        error: "Aucune position de départ enregistrée",
        isTracking: false,
      }));
      
      notifyError("Aucune position de départ enregistrée");
      return;
    }

    try {
      const position = await getCurrentPosition();
      const distance = calculateDistance(state.startPosition, position);
      
      setState((prev) => ({
        ...prev,
        endPosition: position,
        progress: null, // Clear progress when tracking stops
        distance,
        isTracking: false,
        error: null,
      }));
      
      notifyDistanceCalculated(distance);
    } catch (error: any) {
      setState((prev) => ({ ...prev, error: error.message, isTracking: false }));
      notifyError(error.message);
    }
  }, [state.startPosition, getCurrentPosition, notifyDistanceCalculated, notifyError]);

  // Reset tracking
  const resetTracking = useCallback(() => {
    setState({
      startPosition: null,
      endPosition: null,
      distance: null,
      isTracking: false,
      error: null,
      progress: null, // Reset progress
    });
    
    notifyReset();
  }, [notifyReset]);

  return {
    ...state,
    isGeolocationAvailable,
    permissionGranted,
    startTracking,
    stopTracking,
    resetTracking,
  };
};
